import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAnalytics } from "../hooks/useAnalytics";
import { trackPageView } from "../utils/googleAnalytics";

// Background component (same as main portfolio)
function Background() {
  return (
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-[#050a11]">
        <div
            className="absolute inset-0 min-h-full"
            style={{
              backgroundImage: `
            radial-gradient(60rem 60rem at 15% 10%, rgba(37, 99, 235, 0.4), transparent 50%),
            radial-gradient(50rem 50rem at 85% 30%, rgba(56, 189, 248, 0.3), transparent 50%),
            radial-gradient(70rem 70rem at 50% 80%, rgba(14, 165, 233, 0.3), transparent 50%),
            radial-gradient(40rem 40rem at 10% 65%, rgba(147, 51, 234, 0.25), transparent 50%),
            radial-gradient(45rem 45rem at 85% 70%, rgba(236, 72, 153, 0.2), transparent 50%),
            radial-gradient(35rem 35rem at 60% 20%, rgba(34, 197, 94, 0.15), transparent 50%)
          `,
            }}
        />
        <div className="absolute inset-0 min-h-full bg-gradient-to-b from-transparent via-black/10 to-black/30" />
      </div>
  );
}

export default function HireMe() {
  const [solutionRevealed, setSolutionRevealed] = useState(false);
  const analytics = useAnalytics();

  useEffect(() => {
    trackPageView('/hire-me', 'Hire Me - Easter Egg');
    analytics.trackEvent('easter_egg_visit', { page: 'hire-me' });
  }, [analytics]);

  const handleReveal = () => {
    setSolutionRevealed(true);
    analytics.trackEvent('easter_egg_solution_reveal', { page: 'hire-me' });
  };

  const codeSnippet = `// Bug: Application sent. No response.

async function hireNivRomano() {
  console.log("Reviewing application ...");

  // Async check for the perfect candidate
  const isPerfectFit = Promise.resolve(true);

  // A Promise, waiting for 'true' value.
  if (isPerfectFit) { 
    return "Scheduling a callback!";
  }
}`;

  return (
      <div className="min-h-screen bg-transparent text-zinc-100 antialiased">
        <Background />

        <div className="relative z-10 flex flex-col items-center justify-center min-h-[100dvh] px-4 py-6 sm:py-16 lg:py-20">
          {/* Title */}
          <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-center mb-8 sm:mb-12 lg:mb-16"
          >
            Can you find the bug?
          </motion.h1>

          {/* Card */}
          <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              className="w-full max-w-3xl"
          >
            <div className="rounded-2xl overflow-hidden bg-[#0b1220] border border-white/10 shadow-2xl shadow-blue-500/20">
                {/* Top Bar */}
                <div className="bg-[#050a11] px-4 sm:px-6 py-3 border-b border-white/10 flex items-center justify-between">
                  <span className="text-sm font-medium text-zinc-300">JavaScript</span>
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                  </div>
                </div>

                {/* Code Block */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <pre className="text-xs sm:text-sm overflow-x-auto max-w-full">
                    <code className="font-mono text-zinc-300 leading-relaxed block whitespace-pre">
                      {codeSnippet.split('\n').map((line, i) => (
                          <div key={i} className="min-h-[1.5em]">
                            {line.startsWith('//') ? (
                                <span className="text-green-400">{line}</span>
                            ) : line.includes('async function') || line.includes('const') || line.includes('if') || line.includes('return') ? (
                                <span>
                                  {line.split(/(\basync\b|\bfunction\b|\bconst\b|\bif\b|\breturn\b)/).map((part, j) => (
                                      ['async', 'function', 'const', 'if', 'return'].includes(part) ? (
                                          <span key={j} className="text-purple-400">{part}</span>
                                      ) : (
                                          <span key={j}>{part}</span>
                                      )
                                  ))}
                                </span>
                            ) : line.includes('"') ? (
                                <span>
                                  {line.split(/("[^"]*")/).map((part, j) =>
                                      part.startsWith('"') ? (
                                          <span key={j} className="text-yellow-300">{part}</span>
                                      ) : (
                                          <span key={j}>{part}</span>
                                      )
                                  )}
                                </span>
                            ) : (
                                line
                            )}
                          </div>
                      ))}
                    </code>
                  </pre>
                </div>

                {/* Button */}
                <div className="px-4 sm:px-6 lg:px-8 pb-6">
                  <motion.button
                      onClick={handleReveal}
                      disabled={solutionRevealed}
                      whileHover={!solutionRevealed ? { scale: 1.02 } : {}}
                      whileTap={!solutionRevealed ? { scale: 0.98 } : {}}
                      className={`w-full sm:w-auto px-6 py-3 rounded-full border ${
                          solutionRevealed
                              ? 'border-green-500/50 bg-green-500/10 text-green-300 cursor-default'
                              : 'border-white/20 hover:bg-white/5 text-white'
                      } font-medium transition-all text-sm sm:text-base flex items-center justify-center gap-2 mx-auto`}
                  >
                    {solutionRevealed ? (
                        <>
                          <span>Solution Revealed</span>
                          <span className="text-lg">✓</span>
                        </>
                    ) : (
                        <>
                          <span>Reveal Solution</span>
                          <span className="text-lg">🔍</span>
                        </>
                    )}
                  </motion.button>
                </div>

                {/* Solution Section */}
                <AnimatePresence>
                  {solutionRevealed && (
                      <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: "easeInOut" }}
                          onAnimationComplete={() => {
                            window.scrollTo({
                              top: document.documentElement.scrollHeight,
                              behavior: 'smooth'
                            });
                          }}
                          className="overflow-hidden border-t border-white/10"
                      >
                        <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-b from-blue-500/5 to-transparent">
                          {/* Solution Text */}
                          <div className="text-sm sm:text-base text-zinc-300 mb-2 leading-relaxed text-center">
                            <p className="font-semibold text-blue-200 mb-2">You guessed it! await is missing before Promise.</p>
                            <p className="text-zinc-200 font-semibold text-xs sm:text-sm lg:text-base">
                              <code className="text-purple-300 font-mono">hireNivRomano()</code> waiting for a callback.
                            </p>
                          </div>

                          {/* Call to Action */}
                          <div className="text-center my-3 sm:my-3">
                            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-300">
                              You can fix this!
                            </p>
                          </div>

                          {/* Contact Info */}
                          <div className="space-y-2 sm:space-y-5">
                            {/* Separator Line */}
                            <div className="border-b border-white/10"></div>

                            {/* Main Title */}
                            <div className="text-center">
                              <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
                                Niv Romano<br />
                                Full Stack & ML Developer
                              </h2>
                            </div>

                            {/* Contact Links - Centered Container */}
                            <div className="flex flex-col space-y-1 sm:space-y-5 mx-auto w-fit">
                              {/* Portfolio */}
                              <div className="flex flex-row items-center gap-1.5 sm:gap-4">
                                <span className="text-zinc-400 text-xs sm:text-base w-20 sm:w-32 flex-shrink-0">Portfolio</span>
                                <a
                                    href="https://nivromano.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => analytics.trackExternalLink('demo', 'hire-me-portfolio')}
                                    className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/30 hover:decoration-blue-300 transition-colors text-sm sm:text-base inline-flex items-center gap-1 break-all"
                                >
                                  nivromano.com
                                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3ZM5 5h5v2H7v10h10v-3h2v5H5V5Z" />
                                  </svg>
                                </a>
                              </div>

                              {/* LinkedIn */}
                              <div className="flex flex-row items-center gap-1.5 sm:gap-4">
                                <span className="text-zinc-400 text-xs sm:text-base w-20 sm:w-32 flex-shrink-0">LinkedIn</span>
                                <a
                                    href="https://www.linkedin.com/in/niv-romano/"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => analytics.trackExternalLink('linkedin', 'hire-me')}
                                    className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/30 hover:decoration-blue-300 transition-colors text-sm sm:text-base inline-flex items-center gap-1 break-all"
                                >
                                  linkedin.com/in/niv-romano
                                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3ZM5 5h5v2H7v10h10v-3h2v5H5V5Z" />
                                  </svg>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                  )}
                </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
  );
}
